import { t } from "@lingui/macro";
import {
  CreateDataForProjectTimeBillingDocument,
  type CreateDataForProjectTimeBillingQuery,
  type CreateDataForProjectTimeBillingQueryVariables,
  CreateProjectTimeBillingDocument,
  type CreateProjectTimeBillingMutation,
  type CreateProjectTimeBillingMutationVariables,
  DeleteProjectTimeBillingDocument,
  type DeleteProjectTimeBillingMutation,
  type DeleteProjectTimeBillingMutationVariables,
  LockProjectTimeBillingDocument,
  type LockProjectTimeBillingMutation,
  type LockProjectTimeBillingMutationVariables,
  ProjectTimeBillingDetailDocument,
  type ProjectTimeBillingDetailFragment,
  type ProjectTimeBillingDetailQuery,
  type ProjectTimeBillingDetailQueryVariables,
  type ProjectTimeBillingListItemFragment,
  ProjectTimeBillingsDocument,
  type ProjectTimeBillingsQuery,
  type ProjectTimeBillingsQueryVariables,
  QueryProjectTimeBillingsOrderByColumn,
  SortOrder,
  TimeBillingTypeEnum,
  UnlockProjectTimeBillingDocument,
  type UnlockProjectTimeBillingMutation,
  type UnlockProjectTimeBillingMutationVariables,
  UpdateProjectTimeBillingDocument,
  type UpdateProjectTimeBillingInput,
  type UpdateProjectTimeBillingMutation,
  type UpdateProjectTimeBillingMutationVariables,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import { toApiDate } from "@src/utils/dates";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { action, computed, makeObservable, observable } from "mobx";
import { CreateModalStore } from "./components/create-modal/store";
import { DistributionsModalStore } from "./components/distributions-modal/store";
import { SourcesEditModalStore } from "./components/sources-edit-modal/store";

export const UNSAVED_ID_PREFIX = "unsaved-";

export function isUnsavedId(id: string): boolean {
  return id.startsWith(UNSAVED_ID_PREFIX);
}

function generateUnsavedId(): string {
  return `${UNSAVED_ID_PREFIX}${Date.now()}`;
}

export class TimeBillingStore implements BaseStore {
  readonly tableKey = "project-time-billing-table";
  appStore: AppStore;
  projectId: string;

  @observable
  items: ProjectTimeBillingListItemFragment[] = [];

  @observable
  expandedItemIds = new Set<string>();

  @observable
  expandedItemDetails = new Map<string, ProjectTimeBillingDetailFragment>();

  @observable.ref
  total = 0;

  @observable
  editingItemId: string | null = null;

  @observable
  lockingItemIds = new Set<string>();

  @observable
  detailLoadingItemIds = new Set<string>();

  readonly createModalStore!: CreateModalStore;
  readonly sourcesEditModalStore!: SourcesEditModalStore;
  readonly distributionsModalStore!: DistributionsModalStore;
  deleteConfirmState = new DisclosureState<{ id: string }>();

  private listingFetcher = new FetchHelper<
    ProjectTimeBillingsQuery,
    ProjectTimeBillingsQueryVariables
  >(ProjectTimeBillingsDocument);

  private detailFetcher = new FetchHelper<
    ProjectTimeBillingDetailQuery,
    ProjectTimeBillingDetailQueryVariables
  >(ProjectTimeBillingDetailDocument);

  private createDataFetcher = new FetchHelper<
    CreateDataForProjectTimeBillingQuery,
    CreateDataForProjectTimeBillingQueryVariables
  >(CreateDataForProjectTimeBillingDocument);

  private createMutator = new MutationHelper<
    CreateProjectTimeBillingMutation,
    CreateProjectTimeBillingMutationVariables
  >(CreateProjectTimeBillingDocument);

  private updateMutator = new MutationHelper<
    UpdateProjectTimeBillingMutation,
    UpdateProjectTimeBillingMutationVariables
  >(UpdateProjectTimeBillingDocument);

  private lockMutator = new MutationHelper<
    LockProjectTimeBillingMutation,
    LockProjectTimeBillingMutationVariables
  >(LockProjectTimeBillingDocument);

  private unlockMutator = new MutationHelper<
    UnlockProjectTimeBillingMutation,
    UnlockProjectTimeBillingMutationVariables
  >(UnlockProjectTimeBillingDocument);

  private deleteMutator = new MutationHelper<
    DeleteProjectTimeBillingMutation,
    DeleteProjectTimeBillingMutationVariables
  >(DeleteProjectTimeBillingDocument);

  constructor(projectId: string, appStore: AppStore) {
    makeObservable(this);
    this.projectId = projectId;
    this.appStore = appStore;
    this.createModalStore = new CreateModalStore(this);
    this.sourcesEditModalStore = new SourcesEditModalStore(projectId);
    this.distributionsModalStore = new DistributionsModalStore(projectId);
    this.fetchListing();
  }

  @computed
  get hasUnsavedChanges(): boolean {
    return this.editingItemId !== null;
  }

  @computed
  get isListingLoading(): boolean {
    return this.listingFetcher.isLoading.value;
  }

  @computed
  get isCreateLoading(): boolean {
    return this.createMutator.isLoading.value;
  }

  @computed
  get isCreateDataLoading(): boolean {
    return this.createDataFetcher.isLoading.value;
  }

  @computed
  get isUpdateLoading(): boolean {
    return this.updateMutator.isLoading.value;
  }

  @computed
  get isLockLoading(): boolean {
    return this.lockMutator.isLoading.value;
  }

  @computed
  get isUnlockLoading(): boolean {
    return this.unlockMutator.isLoading.value;
  }

  @computed
  get isDeleteLoading(): boolean {
    return this.deleteMutator.isLoading.value;
  }

  @computed
  get isItemDetailLoading(): (id: string) => boolean {
    return (id: string) => this.detailLoadingItemIds.has(id);
  }

  @computed
  get isItemLoading(): (id: string) => boolean {
    return (id: string) => this.lockingItemIds.has(id);
  }

  @action.bound
  async fetchListing(page = 1): Promise<void> {
    const [data, error] = await this.listingFetcher.fetch({
      projectId: this.projectId,
      first: 2000,
      page,
      orderBy: [
        {
          column: QueryProjectTimeBillingsOrderByColumn.CreatedAt,
          order: SortOrder.Desc,
        },
      ],
    });

    if (error || !data.projectTimeBillings) return;

    this.items = data.projectTimeBillings.data;
    this.total = data.projectTimeBillings.paginatorInfo.total;
  }

  @action.bound
  async fetchDetail(id: string): Promise<void> {
    this.detailLoadingItemIds.add(id);

    const [data, error] = await this.detailFetcher.fetch({ id }, null);

    this.detailLoadingItemIds.delete(id);

    if (error || !data.projectTimeBilling) return;

    this.expandedItemDetails.set(id, data.projectTimeBilling);
  }

  @action.bound
  async createAndEdit(dateFrom: string, dateTo: string): Promise<boolean> {
    const [createData, createDataError] = await this.createDataFetcher.fetch({
      projectId: this.projectId,
      dateFrom,
      dateTo,
    });

    if (createDataError || !createData.createDataForProjectTimeBilling)
      return false;

    // Generate unique ID for this unsaved item
    const unsavedId = generateUnsavedId();

    // Map preview data to detail format (without persisting)
    const previewDetail = this.mapComputedToDetail(
      createData.createDataForProjectTimeBilling,
      unsavedId,
      dateFrom,
      dateTo,
    );

    // Store as unsaved detail
    this.expandedItemDetails.set(unsavedId, previewDetail);

    // Add placeholder to items list
    this.items = [previewDetail, ...this.items];

    // Expand and enable edit mode
    this.expandedItemIds.add(unsavedId);
    this.expandedItemIds = new Set(this.expandedItemIds);
    this.editingItemId = unsavedId;

    return true;
  }

  @action.bound
  async update(input: UpdateProjectTimeBillingInput): Promise<boolean> {
    const [data, error] = await this.updateMutator.mutate({ input });

    if (error || !data.updateProjectTimeBilling) return false;

    this.appStore.UIStore.toast({
      status: "success",
      title: t`Time billing updated successfully.`,
    });

    const updated = data.updateProjectTimeBilling;
    this.updateItemAfterMutation(updated);

    // Exit edit mode
    this.editingItemId = null;

    return true;
  }

  @action.bound
  async create(
    unsavedId: string,
    input: CreateProjectTimeBillingMutationVariables["input"],
  ): Promise<ProjectTimeBillingDetailFragment | null> {
    const [data, error] = await this.createMutator.mutate({ input });

    if (error || !data.createProjectTimeBilling) return null;

    this.appStore.UIStore.toast({
      status: "success",
      title: t`Time billing created successfully.`,
    });

    const newItem = data.createProjectTimeBilling;

    // Remove unsaved placeholder and add real item
    this.items = this.items.filter((i) => i.id !== unsavedId);
    this.items = [newItem, ...this.items];
    this.total += 1;

    // Update details map
    this.expandedItemDetails.delete(unsavedId);
    this.expandedItemDetails.set(newItem.id, newItem);

    // Update expanded state
    this.expandedItemIds.delete(unsavedId);
    this.expandedItemIds.add(newItem.id);

    this.editingItemId = null;

    return newItem;
  }

  @action.bound
  async lock(id: string): Promise<boolean> {
    this.lockingItemIds.add(id);

    const [data, error] = await this.lockMutator.mutate({ id });

    this.lockingItemIds.delete(id);

    if (error || !data.lockProjectTimeBilling) return false;

    this.appStore.UIStore.toast({
      status: "success",
      title: t`Time billing locked.`,
    });

    this.updateItemAfterMutation(data.lockProjectTimeBilling);
    return true;
  }

  @action.bound
  async unlock(id: string): Promise<boolean> {
    this.lockingItemIds.add(id);

    const [data, error] = await this.unlockMutator.mutate({ id });

    this.lockingItemIds.delete(id);

    if (error || !data.unlockProjectTimeBilling) return false;

    this.appStore.UIStore.toast({
      status: "success",
      title: t`Time billing unlocked.`,
    });

    this.updateItemAfterMutation(data.unlockProjectTimeBilling);
    return true;
  }

  @action.bound
  async delete(id: string): Promise<boolean> {
    if (isUnsavedId(id)) {
      this.removeUnsavedItem(id);
      this.appStore.UIStore.toast({
        status: "success",
        title: t`Time billing deleted.`,
      });
      return true;
    }

    const [data, error] = await this.deleteMutator.mutate({ id });

    if (error || !data.deleteProjectTimeBilling) return false;

    this.appStore.UIStore.toast({
      status: "success",
      title: t`Time billing deleted.`,
    });

    this.deleteConfirmState.onClose();
    this.expandedItemIds.delete(id);
    this.expandedItemDetails.delete(id);
    if (this.editingItemId === id) {
      this.editingItemId = null;
    }
    await this.fetchListing();
    return true;
  }

  @action.bound
  toggleExpandedItem(id: string): void {
    if (this.expandedItemIds.has(id)) {
      this.expandedItemIds.delete(id);
      // Exit edit mode when collapsing
      if (this.editingItemId === id) {
        this.editingItemId = null;
      }
    } else {
      this.expandedItemIds.add(id);

      // Load detail if not already loaded
      if (this.expandedItemDetails.has(id)) return;
      this.fetchDetail(id);
    }
  }

  @action.bound
  startEditing(id: string): void {
    this.editingItemId = id;
  }

  @action.bound
  cancelEditing(): void {
    this.editingItemId = null;
  }

  @action.bound
  removeUnsavedItem(unsavedId: string): void {
    this.items = this.items.filter((i) => i.id !== unsavedId);
    this.expandedItemIds.delete(unsavedId);
    this.expandedItemDetails.delete(unsavedId);
  }

  @action.bound
  private updateItemAfterMutation(
    data: ProjectTimeBillingDetailFragment,
  ): void {
    this.expandedItemDetails.set(data.id, data);

    const index = this.items.findIndex((item) => item.id === data.id);

    if (index === -1) return;

    this.items.splice(index, 1, data);
  }

  private mapComputedToDetail(
    computed: NonNullable<
      CreateDataForProjectTimeBillingQuery["createDataForProjectTimeBilling"]
    >,
    id: string,
    dateFrom: string,
    dateTo: string,
  ): ProjectTimeBillingDetailFragment {
    const currentUser = this.appStore.authStore.user;

    return {
      id,
      title: null,
      date_from: dateFrom,
      date_to: dateTo,
      type: TimeBillingTypeEnum.Auto,
      final_total_amount: computed.computed_total_amount,
      final_total_note: null,
      is_locked: false,
      lockedByUser: null,
      locked_at: null,
      createdByUser: {
        id: currentUser?.id ?? "",
        full_name: currentUser?.full_name ?? "",
      },
      created_at: toApiDate(new Date()),
      sources: computed.sources.map((s) => ({
        source_type: s.source_type,
        source_id: s.source_id,
        outgoing_invoice_id: s.outgoing_invoice_id,
        budget_item_id: s.budget_item_id,
        budget_item_type: s.budget_item_type,
        amount: s.amount,
        expenseItem: s.expenseItem,
        outgoingInvoice: s.outgoingInvoice,
        budgetItem: s.budgetItem,
        expense: s.expense,
      })),
      components: computed.components.map((c) => ({
        key: c.key,
        amount: c.amount,
        use_in_total: c.use_in_total,
      })),
      userRows: computed.userRows.map((r, idx) => ({
        id: `preview-${idx}`,
        user: r.user,
        position: r.position,
        team: r.team,
        tracked_time: r.tracked_time,
        tracked_hours: r.tracked_hours,
        client_rate: r.client_rate,
        spent: r.spent,
        effective_hourly_rate: r.effective_hourly_rate,
        amount: r.amount,
        invoiced_time: r.invoiced_time,
        overserviced_time: r.overserviced_time,
        effective_hourly_rate_invoiced: r.effective_hourly_rate_invoiced,
      })),
      distributions: computed.distributions.map(
        ({ time_tracking_item_id }) => ({
          time_tracking_item_id,
        }),
      ),
    };
  }

  @action.bound
  openDeleteConfirm(id: string): void {
    this.deleteConfirmState.onOpen({ id });
  }

  @action.bound
  initializeFromUrlParams(idToExpand: string | undefined): void {
    if (!idToExpand) return;
    this.expandedItemIds.add(idToExpand);
    this.fetchDetail(idToExpand);
  }
}
